import { useState } from 'react';
import { useAssistantStore } from '@/stores/useAssistantStore';
import { useCerebroAPI } from '@/hooks/useCerebroAPI';
import { Command, Volume2, Wand2, Music, Settings, Trash2 } from 'lucide-react';

const COMMAND_PRESETS = [
  {
    category: 'Transport',
    icon: Music,
    commands: [
      { label: 'Play', action: 'transport.play', params: {} },
      { label: 'Stop', action: 'transport.stop', params: {} },
      { label: 'Record', action: 'transport.record', params: {} },
      { label: 'Loop Toggle', action: 'transport.loop', params: {} },
    ]
  },
  {
    category: 'Track',
    icon: Settings,
    commands: [
      { label: 'Mute Track 1', action: 'track.mute', params: { target: 'track_1' } },
      { label: 'Solo Track 2', action: 'track.solo', params: { target: 'track_2' } },
      { label: 'Arm Track 3', action: 'track.arm', params: { target: 'track_3' } },
    ]
  },
  {
    category: 'Effects',
    icon: Wand2,
    commands: [
      { label: 'Load Reverb', action: 'effect.load', params: { effect_id: 'reverb_plate' } },
      { label: 'Load EQ', action: 'effect.load', params: { effect_id: 'eq_parametric' } },
      { label: 'Load Compressor', action: 'effect.load', params: { effect_id: 'comp_vca' } },
    ]
  },
  {
    category: 'Voice',
    icon: Volume2,
    commands: [
      { label: 'TTS: Online', action: 'tts', params: { text: 'CEREBRØ_X online.' } },
      { label: 'TTS: Ready', action: 'tts', params: { text: 'Ready for commands.' } },
      { label: 'TTS: Authority', action: 'tts', params: { text: 'Authority confirmed.', voice: 'fiona' } },
    ]
  },
];

export function CommandPalette() {
  const { sendCommand, sendDAW, sendTTS } = useCerebroAPI();
  const { messages, clearMessages } = useAssistantStore();
  const [customCommand, setCustomCommand] = useState('');

  const handlePreset = async (cmd: { label: string; action: string; params: Record<string, unknown> }) => {
    if (cmd.action === 'tts') {
      await sendTTS(cmd.params.text as string, (cmd.params.voice as string) || 'fiona');
    } else if (cmd.action.startsWith('transport.') || cmd.action.startsWith('track.') || cmd.action.startsWith('effect.')) {
      await sendDAW(cmd.action, (cmd.params.target as string) || 'global', cmd.params);
    } else {
      await sendCommand(cmd.label);
    }
  };

  const handleCustom = async () => {
    if (!customCommand.trim()) return;
    await sendCommand(customCommand);
    setCustomCommand('');
  };

  return (
    <div className="bg-void/95 border border-border-subtle rounded-xl overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border-subtle bg-surface/30">
        <Command size={14} className="text-gold" />
        <span className="text-label text-gold">COMMAND PALETTE</span>
        <button
          onClick={clearMessages}
          className="ml-auto p-1 rounded-md text-tertiary hover:text-fire hover:bg-fire/10 transition-all"
          title="Clear history"
        >
          <Trash2 size={12} />
        </button>
      </div>

      <div className="p-3 space-y-3">
        {/* Custom Command */}
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={customCommand}
            onChange={(e) => setCustomCommand(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCustom()}
            placeholder="Enter natural language command..."
            className="flex-1 bg-surface border border-border-subtle rounded-lg px-3 py-2 text-sm text-primary placeholder:text-tertiary focus:border-gold focus:outline-none"
          />
          <button
            onClick={handleCustom}
            disabled={!customCommand.trim()}
            className="p-2 rounded-lg bg-gold/15 border border-gold/30 text-gold hover:bg-gold/25 disabled:opacity-30 transition-all"
          >
            <Command size={16} />
          </button>
        </div>

        {/* Preset Categories */}
        {COMMAND_PRESETS.map((cat) => (
          <div key={cat.category}>
            <div className="flex items-center gap-1.5 mb-1.5">
              <cat.icon size={12} className="text-tertiary" />
              <span className="text-label text-tertiary">{cat.category}</span>
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              {cat.commands.map((cmd) => (
                <button
                  key={cmd.label}
                  onClick={() => handlePreset(cmd)}
                  className="px-2 py-1.5 rounded-md bg-surface/30 text-mono-sm text-secondary hover:bg-hover hover:text-primary transition-all text-left"
                >
                  {cmd.label}
                </button>
              ))}
            </div>
          </div>
        ))}

        {/* Recent Commands */}
        {messages.filter(m => m.metadata?.type === 'command' && m.role === 'user').length > 0 && (
          <div className="pt-2 border-t border-border-subtle">
            <span className="text-label text-tertiary">RECENT</span>
            <div className="mt-1 space-y-1">
              {messages
                .filter(m => m.metadata?.type === 'command' && m.role === 'user')
                .slice(-5)
                .map((m) => (
                  <button
                    key={m.id}
                    onClick={() => sendCommand(m.content)}
                    className="w-full text-left px-2 py-1 rounded-md text-mono-sm text-secondary hover:bg-hover hover:text-primary transition-all truncate"
                  >
                    {m.content}
                  </button>
                ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
